import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import type { Practice, Quote, TodayPlan } from '../api/types';
import { MascotWelcome } from '../components/MascotWelcome';
import { useUser } from '../state/UserContext';
import { PRACTICE_TYPE_ICONS, PRACTICE_TYPE_LABELS } from '../api/types';
import { TRADITION_LABELS } from '../theme/tokens';

const WELCOME_SHOWN_KEY = 'omnist.welcomeShownDate';

function todayString(): string {
  return new Date().toISOString().slice(0, 10);
}

export function Home() {
  const { me } = useUser();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [todayPlan, setTodayPlan] = useState<TodayPlan | null>(null);
  const [todayPractice, setTodayPractice] = useState<Practice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    // Today's recommended practice and today's quotes are independent failure domains --
    // a quotes hiccup shouldn't take down the primary "begin practice" action, and vice versa.
    Promise.all([api.getTodayQuotes(), api.getToday(), api.getPractices({})])
      .then(([quotesRes, planRes, practicesRes]) => {
        if (cancelled) return;
        setQuotes(quotesRes.quotes);
        setTodayPlan(planRes);
        setTodayPractice(practicesRes.practices.find((p) => p.practiceId === planRes.practiceId) ?? null);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load your day.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    try {
      if (localStorage.getItem(WELCOME_SHOWN_KEY) !== todayString()) {
        setShowWelcome(true);
      }
    } catch {
      // localStorage unavailable -- just skip the popup rather than error.
    }
  }, []);

  function dismissWelcome() {
    setShowWelcome(false);
    try {
      localStorage.setItem(WELCOME_SHOWN_KEY, todayString());
    } catch {
      // ignore
    }
  }

  if (loading) {
    return (
      <div className="center-page" style={{ minHeight: '40vh' }}>
        <div className="spinner" />
        <p className="page-subtitle">Loading today&rsquo;s practice...</p>
      </div>
    );
  }

  // Feature the user's chosen main practice's quote first, when they have one.
  const orderedQuotes = me?.primaryTradition
    ? [...quotes].sort((a, b) =>
        a.tradition === me.primaryTradition ? -1 : b.tradition === me.primaryTradition ? 1 : 0
      )
    : quotes;

  return (
    <div>
      {showWelcome && me && (
        <MascotWelcome
          gender={me.gender}
          displayName={me.displayName || 'friend'}
          currentStreak={me.currentStreak}
          primaryTradition={me.primaryTradition}
          onDismiss={dismissWelcome}
        />
      )}

      <h1 className="page-title">
        {me?.displayName ? `Welcome back, ${me.displayName}` : 'Welcome back'}
      </h1>
      <p className="page-subtitle">Here is your day.</p>

      {error && <p className="error-text">{error}</p>}

      {todayPlan && (
        <div className="themed-card today-card" style={{ marginBottom: 20 }}>
          {todayPractice ? (
            <>
              <span className="today-card-label">
                {todayPlan.reason === 'review'
                  ? 'A familiar return'
                  : todayPlan.reason === 'premium_preview'
                    ? 'Your next step'
                    : "Today's practice"}
              </span>
              <h2 className="today-card-title">
                <span aria-hidden="true">{PRACTICE_TYPE_ICONS[todayPractice.type]}</span> {todayPractice.title}
              </h2>
              <p className="page-subtitle" style={{ margin: '4px 0 10px' }}>
                {TRADITION_LABELS[todayPractice.tradition] ?? todayPractice.tradition} &middot;{' '}
                {PRACTICE_TYPE_LABELS[todayPractice.type]} &middot; {Math.round(todayPractice.durationSeconds / 60)} min
              </p>
              <p className="today-card-explanation">{todayPlan.explanation}</p>
              <Link to={`/practice/${todayPractice.practiceId}`} className="btn btn-primary btn-block" style={{ marginTop: 12 }}>
                Begin today&rsquo;s practice
              </Link>
            </>
          ) : (
            <>
              <span className="today-card-label">Today</span>
              <p className="today-card-explanation">{todayPlan.explanation}</p>
              {todayPlan.reason === 'none' && (
                <Link to="/settings" className="btn btn-secondary btn-block" style={{ marginTop: 12 }}>
                  Go to Settings
                </Link>
              )}
            </>
          )}
        </div>
      )}

      <div className="section-title">Today&rsquo;s quote{orderedQuotes.length > 1 ? 's' : ''}</div>
      {orderedQuotes.map((q) => (
        <div key={q.quoteId} className="themed-card" style={{ marginBottom: 14 }}>
          <p className="quote-text">&ldquo;{q.text}&rdquo;</p>
          <p className="quote-author">&mdash; {q.author}</p>
          <span className="quote-tradition-tag">{TRADITION_LABELS[q.tradition] ?? q.tradition}</span>
        </div>
      ))}
    </div>
  );
}
